import type { Block } from "@/components/legal/LegalContent";
import type { LocaleCode } from "./types";
import { da } from "./translations/da";
import { de } from "./translations/de";
import { es } from "./translations/es";
import { fr } from "./translations/fr";
import { it } from "./translations/it";
import { nl } from "./translations/nl";
import { no } from "./translations/no";
import { sv } from "./translations/sv";

/**
 * Locale-keyed bodies for the curated Help Centre articles.
 *
 * The English body in `articles.ts` is the canonical source. This map holds
 * locale-specific overrides keyed by `article slug → locale → Block[]`; any
 * slug/locale that is absent falls back to the English body via
 * `getArticleBody(article, locale)` in `articles.ts`.
 *
 * The per-locale bodies live in `./translations/<locale>.ts` (one module per
 * language, keyed by article slug) and are assembled into the slug→locale map
 * below. Splitting per locale keeps each translation file reviewable and lets
 * the translation pass be worked on one language at a time.
 *
 * Inline tokens (`{{brand}}`, `{{supportEmail}}`) and inline `[label](href)`
 * link micro-syntax are preserved block-for-block against the English source,
 * so `renderBlocks` and the Intercom sync interpolate / render them identically
 * across locales. Structural parity with the English body is enforced by
 * `scripts/check-help-translations.mjs`.
 */
const BY_LOCALE: Partial<Record<LocaleCode, Record<string, Block[]>>> = {
  da,
  de,
  es,
  fr,
  it,
  nl,
  no,
  sv,
};

function assembleTranslations(): Record<string, Partial<Record<LocaleCode, Block[]>>> {
  const out: Record<string, Partial<Record<LocaleCode, Block[]>>> = {};
  for (const [locale, bySlug] of Object.entries(BY_LOCALE) as [
    LocaleCode,
    Record<string, Block[]>,
  ][]) {
    for (const [slug, body] of Object.entries(bySlug)) {
      (out[slug] ??= {})[locale] = body;
    }
  }
  return out;
}

export const ARTICLE_BODY_TRANSLATIONS: Record<
  string,
  Partial<Record<LocaleCode, Block[]>>
> = assembleTranslations();
